/**
 * Burst Limit Indicator Component
 * Separate visual indicator for burst limits with pulse animation
 * Non-intrusive placement with tooltip explanation
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Zap, Info } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import type { RateLimitState } from '../hooks/useChatRateLimit';

interface BurstLimitIndicatorProps {
  rateLimitState: RateLimitState | null;
  className?: string;
  showTooltip?: boolean;
}

/**
 * Burst Limit Indicator Component
 * Shows burst limit status with pulse animation when low
 */
export const BurstLimitIndicator: React.FC<BurstLimitIndicatorProps> = ({
  rateLimitState,
  className,
  showTooltip = true,
}) => {
  const [showTooltipState, setShowTooltipState] = useState(false);

  if (!rateLimitState || rateLimitState.burstRemaining >= rateLimitState.remaining) {
    return null;
  }

  const { burstRemaining, limit } = rateLimitState;
  const isLow = burstRemaining <= 2;

  return (
    <div className={cn('relative inline-flex items-center', className)}>
      <motion.div
        animate={isLow ? {
          scale: [1, 1.1, 1],
        } : {}}
        transition={{
          duration: 2,
          repeat: isLow ? Infinity : 0,
          ease: 'easeInOut',
        }}
        className={cn(
          'inline-flex items-center gap-1.5 px-2 py-1 rounded-lg',
          'border border-blue-200 dark:border-blue-800',
          'bg-blue-50 dark:bg-blue-950/20',
          'transition-all duration-200',
          isLow && 'border-blue-300 dark:border-blue-700'
        )}
        onMouseEnter={() => showTooltip && setShowTooltipState(true)}
        onMouseLeave={() => setShowTooltipState(false)}
        role="status"
        aria-live="polite"
        aria-label={`Burst limit: ${burstRemaining} remaining`}
      >
        <Zap 
          className={cn(
            'w-3.5 h-3.5',
            isLow ? 'text-blue-600 dark:text-blue-400' : 'text-blue-500 dark:text-blue-500'
          )}
          aria-hidden="true"
        />
        <span className={cn(
          'text-xs font-medium tabular-nums',
          isLow ? 'text-blue-700 dark:text-blue-400' : 'text-blue-600 dark:text-blue-500'
        )}>
          {burstRemaining}
        </span>
      </motion.div>

      {/* Tooltip */}
      <AnimatePresence>
        {showTooltip && showTooltipState && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-50"
          >
            <div className="relative bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg max-w-xs">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold mb-1">Burst Limit</p>
                  <p className="text-gray-300">
                    You're sending messages too quickly. Slow down to avoid hitting the burst limit.
                  </p>
                  <p className="text-gray-400 mt-1 text-[10px]">
                    {burstRemaining} of {limit} burst messages remaining
                  </p>
                </div>
              </div>
              {/* Arrow */}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                <div className="w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45"></div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

